import { useEffect, useMemo, useState } from 'react'
import { Download, FileBarChart } from 'lucide-react'
import { fetchCustomers } from '@/api/customers'
import { exportReportCsv, fetchReport } from '@/api/reports'
import { useAuth } from '@/auth/AuthContext'
import { ColumnPicker } from '@/components/ColumnPicker'
import { CustomerFilter } from '@/components/CustomerFilter'
import { PatchStatusReport } from '@/components/reports/PatchStatusReport'
import { OsBreakdownReport } from '@/components/reports/OsBreakdownReport'
import { ZabbixCoverageReport } from '@/components/reports/ZabbixCoverageReport'
import { ReportTable } from '@/components/ReportTable'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { getReportColumns } from '@/config/columns'
import { useColumnVisibility } from '@/hooks/useColumnVisibility'

const REPORTS = [
  {
    slug: 'asset-list',
    title: 'Asset register',
    description: 'Full device list with OS and online status.',
  },
  {
    slug: 'hardware-summary',
    title: 'Hardware summary',
    description: 'CPU, RAM and specs across devices.',
  },
  {
    slug: 'os-breakdown',
    title: 'OS breakdown',
    description: 'Operating system versions across the estate.',
  },
  {
    slug: 'alerts-summary',
    title: 'Alerts summary',
    description: 'Alert list with device, customer, and dates.',
  },
  {
    slug: 'software-audit',
    title: 'Software audit',
    description: 'Installed software from the daily RMM installed-software snapshot, per device.',
  },
  {
    slug: 'patch-status',
    title: 'Patch status',
    description: 'Patch counts by status; click a row to see updates per device.',
  },
  {
    slug: 'security-summary',
    title: 'Security summary',
    description:
      'Per-device security posture: Defender, ThreatDown, BitLocker, disk, firewall, .NET 6 (EOL), winget, and PAM-lite signals.',
  },
  {
    slug: 'zabbix-coverage',
    title: 'Zabbix coverage',
    description: 'Devices missing from Zabbix, and Zabbix hosts that are not in Bekat Dash.',
    staffOnly: true,
  },
]

export function Reports() {
  const { isCustomer, isStaff, canFilterCustomers } = useAuth()
  const visibleReports = REPORTS.filter((report) => !report.staffOnly || isStaff)
  const [selected, setSelected] = useState(REPORTS[0].slug)
  const [customers, setCustomers] = useState([])
  const [customerIds, setCustomerIds] = useState([])
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')
  const [exportNote, setExportNote] = useState('')

  const reportColumns = useMemo(() => {
    const defined = getReportColumns(selected)
    const base = defined.length
      ? defined
      : rows.length
        ? Object.keys(rows[0]).map((id) => ({
            id,
            label: id.replace(/_/g, ' '),
            defaultVisible: true,
          }))
        : []
    return isCustomer ? base.filter((c) => !c.hideForCustomer) : base
  }, [selected, rows, isCustomer])

  const storageKey = `report-columns-${selected}`
  const {
    visibleIds,
    visibleColumns,
    toggleColumn,
    showAll,
    resetDefaults,
  } = useColumnVisibility(storageKey, reportColumns)

  useEffect(() => {
    if (canFilterCustomers) {
      fetchCustomers().then(setCustomers).catch(() => {})
    }
  }, [canFilterCustomers])

  useEffect(() => {
    if (selected === 'patch-status' || selected === 'os-breakdown' || selected === 'zabbix-coverage') {
      setRows([])
      setLoading(false)
      setExportNote('')
      return
    }
    setLoading(true)
    setError('')
    setExportNote('')
    fetchReport(selected, {}, customerIds)
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch(() => {
        setError('Could not load report.')
        setRows([])
      })
      .finally(() => setLoading(false))
  }, [selected, customerIds])

  async function handleExport() {
    setExporting(true)
    try {
      await exportReportCsv(selected, customerIds)
    } catch {
      setError('Export failed.')
    } finally {
      setExporting(false)
    }
  }

  const activeReport = REPORTS.find((r) => r.slug === selected)

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Report options</CardTitle>
          <CardDescription>
            Filter by one or more customers and choose which columns appear in the preview.
            {exportNote || 'CSV export includes all data columns for the selected report.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            {canFilterCustomers && (
              <CustomerFilter
                id="reports-customer"
                value={customerIds}
                onChange={setCustomerIds}
                customers={customers}
                className="min-w-[220px]"
              />
            )}
            {reportColumns.length > 0 &&
              selected !== 'patch-status' &&
              selected !== 'os-breakdown' &&
              selected !== 'zabbix-coverage' && (
              <ColumnPicker
                columns={reportColumns}
                visibleIds={visibleIds}
                onToggle={toggleColumn}
                onShowAll={showAll}
                onResetDefaults={resetDefaults}
              />
            )}
            {selected !== 'zabbix-coverage' ? (
              <Button variant="outline" onClick={handleExport} disabled={exporting}>
                <Download className="h-4 w-4" />
                {exporting ? 'Exporting…' : 'Export CSV'}
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {visibleReports.map((r) => (
          <Card
            key={r.slug}
            className={`cursor-pointer transition-colors ${
              selected === r.slug ? 'border-primary' : 'hover:border-muted-foreground/40'
            }`}
            onClick={() => setSelected(r.slug)}
          >
            <CardHeader className="pb-2">
              <FileBarChart className="mb-2 h-5 w-5 text-primary" />
              <CardTitle className="text-base">{r.title}</CardTitle>
              <CardDescription>{r.description}</CardDescription>
            </CardHeader>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Preview</CardTitle>
          <CardDescription>{activeReport?.title}</CardDescription>
        </CardHeader>
        <CardContent>
          {error && <p className="mb-4 text-sm text-destructive">{error}</p>}
          {selected === 'patch-status' ? (
            <PatchStatusReport
              customerIds={customerIds}
              onExportNote={setExportNote}
            />
          ) : selected === 'os-breakdown' ? (
            <OsBreakdownReport
              customerIds={customerIds}
              onExportNote={setExportNote}
            />
          ) : selected === 'zabbix-coverage' ? (
            <ZabbixCoverageReport customerIds={customerIds} />
          ) : loading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <ReportTable
              key={selected}
              rows={rows}
              visibleColumns={visibleColumns}
              defaultSort={
                selected === 'alerts-summary'
                  ? { id: 'created', dir: 'desc' }
                  : selected === 'software-audit'
                    ? { id: 'install_date', dir: 'desc' }
                    : undefined
              }
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
